/* gate-dialog.js v1.3.0
 * Loads each membership-gate popup's content on first open.
 *
 * Shopify does not allow a section to render another section, so the styled
 * content (built in the theme editor on the standalone gate page) can't be
 * pulled in via {% section %}/{% sections %} from wherever the popup lives.
 * Instead, this fetches that page's real rendered HTML (same technique as
 * Dawn's own Section Rendering API usage in variant-picker.js) and extracts
 * whichever element matches [id^="shopify-section-"] — avoids hardcoding the
 * exact numeric section id, which is generated by Shopify and not stable
 * across template recreation.
 *
 * Multiple independent dialogs can exist on one page (e.g. the archive
 * calendar block appears more than once on the article template) — each
 * dialog has a unique id ("gate-dialog-{block.id}") and tracks its own
 * loaded state, so they never interfere with each other.
 *
 * Height on short viewports: matches the existing solution-popup/print-popup
 * pattern in this theme (snippets/sudoku-carousel-grid.liquid) — max-height
 * + overflow-y: auto on the dialog itself, not a custom scale-down transform.
 */
(function () {
  "use strict";

  function wireDialog(dlg) {
    var loaded = false;
    dlg.addEventListener("dialog:open", function () {
      /* The native <dialog> auto-focuses its first focusable descendant
       * (.gate-dialog__close) on open and scrolls it into view — but since
       * the close button is position:absolute inside the dialog's own
       * scrollable box, that auto-scroll overshoots (observed: settles at
       * scrollTop 27 instead of 0), cropping the close button and the top
       * of the content. Catch that first scroll event and snap it back. */
      var dialogEl = dlg.querySelector(".gate-dialog__content");
      if (dialogEl) {
        var resetScroll = function () {
          dialogEl.scrollTop = 0;
          dialogEl.removeEventListener("scroll", resetScroll);
        };
        dialogEl.addEventListener("scroll", resetScroll);
      }

      if (loaded) return;
      var body = dlg.querySelector(".gate-dialog__body");
      var url = dlg.dataset.contentUrl;
      if (!body || !url) return;

      fetch(url, { cache: "no-store" })
        .then(function (res) {
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var section = doc.querySelector('[id^="shopify-section-"]');
          if (section) {
            body.innerHTML = section.innerHTML;
            loaded = true;

            /* Full-screen mobile mode fills the backdrop with the popup's own
             * fill color (see gate-dialog--fullscreen-mobile CSS) instead of
             * leaving it transparent, so nothing but that color shows during
             * an iOS elastic-scroll drag. ::backdrop inherits custom
             * properties from its originating element (the dialog) in all
             * major browsers since 2024, so setting it here on dialogEl
             * makes it available to the ::backdrop rule via var(). */
            var fill = body.querySelector(".section-background");
            var dialogEl = dlg.querySelector(".gate-dialog__content");
            if (fill && dialogEl) {
              var bgColor = getComputedStyle(fill).backgroundColor;
              dialogEl.style.setProperty("--gate-dialog-fill-color", bgColor);
            }
          }
        })
        .catch(function () {
          /* leave body empty — close button still works */
        });
    });
  }

  document.addEventListener("DOMContentLoaded", function () {
    var dialogs = document.querySelectorAll('[id^="gate-dialog-"]');
    dialogs.forEach(wireDialog);

    /* Editor-visibility safeguard: a closed <dialog> is invisible by default
     * and this theme has no built-in auto-open-in-editor mechanism. The
     * block sets window.__gateDialogDesignMode only when request.design_mode
     * is true (never on the live storefront). Only the first dialog found is
     * opened, even if the calendar block appears more than once on the page,
     * to avoid stacking multiple popups in the editor at once. */
    if (window.__gateDialogDesignMode && dialogs.length) {
      var first = dialogs[0];
      customElements.whenDefined("dialog-component").then(function () {
        if (first.showDialog) first.showDialog();
      });
    }
  });
})();
